import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "collapsed", "maxHeight"];
import React, { forwardRef, useRef, useEffect, useLayoutEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { motionDurationNormal } from '@resultadosdigitais/tangram-design-tokens';
import { ScrollbarStyle } from '../StyleBase';
const DISPLAY_NAME = 'Collapse';
const TRANSITION_DURATION = parseInt(motionDurationNormal);
// Adds some extra space in `px` to `offsetHeight` when it's open to ensure that the content is not cut off.
const OFFSET = 50;
const Inner = styled.div.withConfig({
  displayName: "Collapse__Inner",
  componentId: "sc-182vtah-0"
})([""]);
const Root = styled.div.withConfig({
  displayName: "Collapse__Root",
  componentId: "sc-182vtah-1"
})(["", " will-change:height;transition-duration:var(--motion-duration-normal);transition-property:max-height;", ""], ScrollbarStyle, _ref => {
  let {
    $state,
    $innerHeight,
    $maxHeight
  } = _ref;
  // 'open': The container is fully expanded with a specified max-height or no limit.
  if ($state === 'open') {
    return css(["max-height:", ";overflow-y:", ";"], $maxHeight || 'none', $maxHeight ? 'auto' : 'initial');
  }

  // 'before-closing': The container is about to close, retaining its max-height or inner height.
  if ($state === 'before-closing') {
    return css(["max-height:", ";overflow-y:", ";pointer-events:none;user-select:none;"], $maxHeight || $innerHeight, $maxHeight ? 'auto' : 'hidden');
  }

  // 'close': The container is fully collapsed with a max-height of 0.
  if ($state === 'close') {
    return css(["max-height:0;overflow-y:", ";pointer-events:none;user-select:none;"], $maxHeight ? 'auto' : 'hidden');
  }

  // 'opening': The container is in the process of opening, retaining its max-height or inner height.
  if ($state === 'opening') {
    return css(["max-height:", ";overflow-y:", ";pointer-events:none;user-select:none;"], $maxHeight || $innerHeight, $maxHeight ? 'auto' : 'hidden');
  }
});
function useCollapse(collapsed) {
  const intervalRef = useRef(null);
  const [state, setState] = useState(collapsed ? 'close' : 'open');
  useLayoutEffect(() => {
    // State machine responsible for handling the transitions between the states.
    if (collapsed) {
      if (['open', 'opening'].includes(state)) {
        // Clear the interval to avoid flickering when the user toggles the collapse state.
        clearInterval(intervalRef.current);
        setState('before-closing');
      }
      if (state === 'before-closing') {
        // `setTimeout` with `0` delay prevents flickering when toggling collapse state.
        intervalRef.current = setTimeout(() => setState('close'), 0);
      }
    } else {
      if (['close', 'before-closing'].includes(state)) {
        clearInterval(intervalRef.current);
        setState('opening');
      }
      if (state === 'opening') {
        intervalRef.current = setTimeout(() => setState('open'), TRANSITION_DURATION);
      }
    }
    return () => clearInterval(intervalRef.current);
  }, [collapsed, state]);
  return state;
}
const Collapse = /*#__PURE__*/forwardRef((_ref2, ref) => {
  var _rest$tabIndex;
  let {
      children,
      collapsed,
      maxHeight: maxHeightProp
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded);
  const innerRef = useRef(null);
  const [innerHeight, setInnerHeight] = useState(undefined);
  const state = useCollapse(collapsed);
  const maxHeight = maxHeightProp ? typeof maxHeightProp === 'string' ? maxHeightProp : "".concat(maxHeightProp, "px") : undefined;
  useEffect(() => {
    var _innerRef$current;
    const offsetHeight = (_innerRef$current = innerRef.current) === null || _innerRef$current === void 0 ? void 0 : _innerRef$current.offsetHeight;
    setInnerHeight(offsetHeight !== undefined ? "".concat(offsetHeight + OFFSET, "px") : undefined);
  }, []);
  return /*#__PURE__*/React.createElement(Root, _extends({}, rest, {
    ref: ref,
    "aria-hidden": collapsed ? 'true' : undefined,
    tabIndex: collapsed ? -1 : (_rest$tabIndex = rest.tabIndex) !== null && _rest$tabIndex !== void 0 ? _rest$tabIndex : undefined,
    $state: state,
    $innerHeight: innerHeight,
    $maxHeight: maxHeight
  }), /*#__PURE__*/React.createElement(Inner, {
    ref: innerRef
  }, children));
});
Collapse.displayName = DISPLAY_NAME;
Collapse.Style = Inner;
Collapse.WrapperStyle = Root;
Collapse.defaultProps = {
  collapsed: false
};
process.env.NODE_ENV !== "production" ? Collapse.propTypes = {
  /**
   * Sets the children.
   */
  children: PropTypes.node,
  /**
   * Sets the collapsed state.
   */
  collapsed: PropTypes.bool,
  /**
   * Sets the max height of the container.
   * If a number, the value will be automatically transformed into pixels.
   * Unlimit if not set.
   */
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
} : void 0;
export default Collapse;